/**
 * What a student is shown of a diagnosis — one place, so DiagnosisBlock and
 * FirstIssueBlock can never word it differently.
 *
 * Both vocabularies are read (docs/FeatureDesigns/diagnosis/accuracy/
 * diagnosis-structure-target.md). Three rules decide what is shown:
 *   - `none` and `undetermined` show nothing: the first has nothing to say,
 *     the second could not tell what went wrong;
 *   - a `tentative` diagnosis is not stated as fact, so it is not shown —
 *     unless a teacher approved it, when the server sends it without its
 *     doubt (`approvedAsStated`, functions selection/withhold.ts);
 *   - `misapplied_rule` (vocabulary 1) is deliberately not called a gap: the
 *     student knows the rule, what they are missing is where it stops applying.
 */
import type { Diagnosis } from '@padcanvas/shared';

const KIND_LABEL: Partial<Record<Diagnosis['kind'], string>> = {
    slip: 'Careless slip',
    wrong_knowledge: 'Misconception',
    missing_knowledge: 'Concept gap',
    improvement: 'Could be better',
    misapplied_rule: 'Right idea, wrong place',
    gap: 'Concept gap',
};

const ANSWER_SHEET_SLIP = 'Answer-sheet slip';

/** What the label needs to know of the question. */
export interface LabelContext {
    /** Answered by option (MCQ, MCQ_MULTI). */
    readonly choice?: boolean;
}

/** The label for a kind at a stage. Vocabulary 1's `encoding` is the same
 *  stage as vocabulary 2's `answer`. */
export function diagnosisLabel(kind: string | undefined, stage?: string, ctx?: LabelContext): string | undefined {
    if (kind === 'slip' && ctx?.choice && (stage === 'answer' || stage === 'encoding')) return ANSWER_SHEET_SLIP;
    return kind ? KIND_LABEL[kind as Diagnosis['kind']] : undefined;
}

export interface StudentDiagnosis {
    readonly kind: Diagnosis['kind'];
    readonly label: string;
    readonly cause: string;
}

/** The diagnosis as a student may see it, or undefined when nothing is to be shown. */
export function diagnosisForStudent(diagnosis: Diagnosis | undefined, ctx?: LabelContext): StudentDiagnosis | undefined {
    if (!diagnosis || diagnosis.certainty === 'tentative') return undefined;
    const label = diagnosisLabel(diagnosis.kind, diagnosis.stage, ctx);
    const cause = diagnosis.cause?.trim() ?? '';
    if (!label || !cause) return undefined;
    return { kind: diagnosis.kind, label, cause };
}

/** The diagnosis as a teacher reviewing it may see it: a tentative cause too,
 *  flagged, since review exists for the ones the system is unsure of. Never
 *  for a student. */
export function diagnosisForReview(diagnosis: Diagnosis | undefined, ctx?: LabelContext): (StudentDiagnosis & { tentative: boolean }) | undefined {
    if (!diagnosis) return undefined;
    const shown = diagnosisForStudent({ ...diagnosis, certainty: undefined }, ctx);
    return shown && { ...shown, tentative: diagnosis.certainty === 'tentative' };
}

/** The plain label for a diagnosis kind, or undefined for one with none
 *  (`none`, `undetermined`). For a reader comparing two readings' kinds. */
export function diagnosisKindLabel(kind: string | undefined): string | undefined {
    return kind ? KIND_LABEL[kind as Diagnosis['kind']] : undefined;
}
